import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { ResponsiveContainer, BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Cell } from "recharts";
import { Lightbulb, RotateCcw, Link2, CheckCircle2, Eye, Target } from "lucide-react";
import { RECORDS, paretoL1, instrumentPerformance, computeInstrumentHealth, ciMigrationByReagent, retestRecoveryByMode, PRODUCTS } from "../data/synthetic";
import { SectionHeader, Disclaimer, usePageMeta, copyToClipboard, tooltipStyle, axisProps } from "./common";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import hunt from "../content/live_pattern_hunt.json";
import mission3raw from "../content/pattern_hunt_mission_three.json";

const GRID = <CartesianGrid stroke="rgba(32,184,205,0.08)" vertical={false} />;
const MB_MODE = "Microbubble-Induced Signal Artifact";

const mission3 = {
  signalId: mission3raw.missionId,
  title: mission3raw.title,
  secretTarget: mission3raw.targetComponentLot,
  product: mission3raw.product,
  metric: mission3raw.failureMode,
  failureMode: mission3raw.failureMode,
  path: mission3raw.guidedSteps.map((s) => ({ step: s.step, prompt: s.prompt, action: s.expectedAction })),
  reveal: mission3raw.reveal.finding,
  lesson: mission3raw.reveal.methodologyLesson,
};

const MISSIONS = [...hunt.signals, mission3];

const MISSION_META = [
  { difficulty: "Beginner", time: "~2 min" },
  { difficulty: "Intermediate", time: "~3 min" },
  { difficulty: mission3raw.difficulty, time: `~${mission3raw.estimatedTimeMinutes} min` },
];

export default function PatternHunt() {
  usePageMeta({
    title: "Live Pattern Hunt | Poojan Patel",
    description: "A guided technical challenge: discover the hidden INS-04, RGT-107 and CMP-209 signals in the deterministic synthetic manufacturing dataset.",
    canonicalPath: "/analytics-lab/pattern-hunt",
  });
  const [searchParams] = useSearchParams();

  const pareto = useMemo(() => paretoL1(RECORDS).slice(0, 8), []);
  const instPerf = useMemo(() => instrumentPerformance(RECORDS), []);
  const ins04 = useMemo(() => computeInstrumentHealth(RECORDS).find((h) => h.instrument === "INS-04"), []);
  const ins04Monthly = useMemo(() => {
    const byMonth = {};
    RECORDS.filter((r) => r.instrument === "INS-04" && r.failure_mode === "Signal Noise Excursion" && r.attempt_type === "Initial").forEach(
      (r) => (byMonth[r.month] = (byMonth[r.month] || 0) + 1)
    );
    return Object.entries(byMonth)
      .sort((a, b) => a[0].localeCompare(b[0]))
      .map(([month, count]) => ({ month, count }));
  }, []);
  const reagents = useMemo(() => ciMigrationByReagent(), []);
  const fleetMean = useMemo(() => {
    const others = reagents.filter((r) => r.lot !== "RGT-107");
    return (others.reduce((s, r) => s + r.mean, 0) / others.length).toFixed(2);
  }, [reagents]);
  const rgt107 = reagents.find((r) => r.lot === "RGT-107");

  // Mission 3 data — NovaFlow-FX × CMP-209 microbubble artifacts
  const cmpLots = useMemo(() => {
    const groups = {};
    RECORDS.filter((r) => r.product === "NovaFlow-FX" && r.attempt_type === "Initial").forEach((r) => {
      groups[r.component_lot] = groups[r.component_lot] || { lot: r.component_lot, runs: 0, artifacts: 0 };
      groups[r.component_lot].runs++;
      if (r.failure_mode === MB_MODE) groups[r.component_lot].artifacts++;
    });
    return Object.values(groups)
      .sort((a, b) => a.lot.localeCompare(b.lot))
      .map((g) => ({ ...g, rate: Number(((g.artifacts / g.runs) * 100).toFixed(1)) }));
  }, []);
  const cmp209 = cmpLots.find((g) => g.lot === "CMP-209");
  const totalArtifacts = cmpLots.reduce((s, g) => s + g.artifacts, 0);
  const cmp209ByInst = useMemo(() => {
    const counts = {};
    RECORDS.filter(
      (r) => r.product === "NovaFlow-FX" && r.component_lot === "CMP-209" && r.failure_mode === MB_MODE && r.attempt_type === "Initial"
    ).forEach((r) => (counts[r.instrument] = (counts[r.instrument] || 0) + 1));
    return Object.entries(counts)
      .sort((a, b) => a[0].localeCompare(b[0]))
      .map(([instrument, count]) => ({ instrument, count }));
  }, []);
  const mbRecovery = useMemo(
    () => retestRecoveryByMode(RECORDS.filter((r) => r.product === "NovaFlow-FX")).find((m) => m.mode === MB_MODE),
    []
  );

  const [phase, setPhase] = useState("intro"); // intro | play | done
  const [mIdx, setMIdx] = useState(0);
  const [sIdx, setSIdx] = useState(0);
  const [doneM, setDoneM] = useState(() => MISSIONS.map(() => false));
  const [revealed, setRevealed] = useState(() => MISSIONS.map(() => false));
  const [showHint, setShowHint] = useState(false);
  const [nudge, setNudge] = useState("");
  const [product, setProduct] = useState("");

  useEffect(() => {
    window.scrollTo(0, 0);
    if (searchParams.get("mission") === "cmp-209") {
      setPhase("play");
      setMIdx(2);
      setSIdx(0);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const mission = MISSIONS[mIdx];
  const step = mission.path[sIdx];
  const totalSteps = MISSIONS.reduce((s, m) => s + m.path.length, 0);
  const stepsDone = MISSIONS.reduce((s, m, i) => s + (doneM[i] ? m.path.length : i === mIdx ? sIdx : 0), 0);

  const missionDone = () => {
    const nd = [...doneM];
    nd[mIdx] = true;
    setDoneM(nd);
    const nextIdx = nd.findIndex((d) => !d);
    if (nextIdx === -1) setPhase("done");
    else {
      setMIdx(nextIdx);
      setSIdx(0);
    }
  };

  const advance = () => {
    setNudge("");
    setShowHint(false);
    setProduct("");
    if (sIdx < mission.path.length - 1) {
      setSIdx(sIdx + 1);
    } else {
      missionDone();
    }
  };

  const correct = () => advance();
  const wrong = () => setNudge("Not quite — the signal concentrates elsewhere. Use the hint if you need it.");

  const showAnswer = () => {
    const nr = [...revealed];
    nr[mIdx] = true;
    setRevealed(nr);
    setNudge("");
    setShowHint(false);
    setProduct("");
    missionDone();
  };

  const reset = () => {
    setPhase("intro");
    setMIdx(0);
    setSIdx(0);
    setDoneM(MISSIONS.map(() => false));
    setRevealed(MISSIONS.map(() => false));
    setShowHint(false);
    setNudge("");
    setProduct("");
  };

  const stepBody = () => {
    // Mission 1 — INS-04 / Signal Noise Excursion
    if (mIdx === 0 && sIdx === 0)
      return (
        <div data-testid="hunt-m1-step1">
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={pareto} margin={{ left: -14, right: 8, top: 4 }}>
              {GRID}
              <XAxis dataKey="mode" {...axisProps} interval={0} angle={-30} textAnchor="end" height={80} />
              <YAxis {...axisProps} />
              <Tooltip {...tooltipStyle} />
              <Bar dataKey="count" name="Failures" cursor="pointer" onClick={(d) => (d && d.mode === "Signal Noise Excursion" ? correct() : wrong())}>
                {pareto.map((e) => (
                  <Cell key={e.mode} fill="#20B8CD" />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
          <p className="font-mono text-[10px] text-steel mt-1">Click the failure mode you would investigate first.</p>
        </div>
      );
    if (mIdx === 0 && sIdx === 1)
      return (
        <div data-testid="hunt-m1-step2">
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={instPerf} margin={{ left: -14, right: 8, top: 4 }}>
              {GRID}
              <XAxis dataKey="instrument" {...axisProps} />
              <YAxis {...axisProps} />
              <Tooltip {...tooltipStyle} formatter={(v, name, item) => [`${v}% — top: ${item.payload.topMode}`, "Failure rate"]} />
              <Bar dataKey="failureRate" name="Failure %" cursor="pointer" onClick={(d) => (d && d.instrument === "INS-04" ? correct() : wrong())}>
                {instPerf.map((e) => (
                  <Cell key={e.instrument} fill="#20B8CD" />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
          <p className="font-mono text-[10px] text-steel mt-1">Click the instrument that stands out.</p>
        </div>
      );
    if (mIdx === 0 && sIdx === 2)
      return (
        <div className="panel p-5 bg-navy" data-testid="hunt-m1-step3">
          <p className="font-mono text-xs text-cyan">INS-04 concentration check</p>
          <p className="text-sm text-pale/85 mt-2 leading-relaxed">
            {ins04.topModeShare.toFixed(0)}% of INS-04 failures are Signal Noise Excursion — against a fleet where no other instrument shows this
            concentration. Failure rate: {ins04.failureRate.toFixed(1)}% across {ins04.runs} initial runs.
          </p>
          <button onClick={correct} data-testid="hunt-m1-confirm" className="mt-4 bg-cyan text-navy font-sans font-semibold text-sm px-5 py-2.5 rounded-sm hover:bg-pale transition-colors">
            Confirm the concentration
          </button>
        </div>
      );
    if (mIdx === 0 && sIdx === 3)
      return (
        <div data-testid="hunt-m1-step4">
          <ResponsiveContainer width="100%" height={200}>
            <LineChart data={ins04Monthly} margin={{ left: -18, right: 8, top: 4 }}>
              {GRID}
              <XAxis dataKey="month" {...axisProps} interval={2} />
              <YAxis {...axisProps} />
              <Tooltip {...tooltipStyle} formatter={(v) => [v, "SNE failures"]} />
              <Line type="monotone" dataKey="count" stroke="#FFB347" strokeWidth={2} dot={false} name="INS-04 SNE failures" />
            </LineChart>
          </ResponsiveContainer>
          <p className="text-xs text-steel mt-2">The signal persists across months — it is not one isolated event.</p>
          <button onClick={correct} data-testid="hunt-m1-complete" className="mt-4 bg-cyan text-navy font-sans font-semibold text-sm px-5 py-2.5 rounded-sm hover:bg-pale transition-colors">
            Pattern verified — complete mission
          </button>
        </div>
      );
    // Mission 2 — RGT-107 / RapidSDS-X migration shift
    if (mIdx === 1 && sIdx === 0)
      return (
        <div data-testid="hunt-m2-step1">
          <p className="text-xs text-steel mb-3">Set the product filter exactly as you would in the Analytics Lab.</p>
          <Select value={product} onValueChange={(v) => { setProduct(v); if (v === "RapidSDS-X") correct(); else wrong(); }}>
            <SelectTrigger className="bg-navy border-teal/40 text-pale text-xs h-9 w-64" data-testid="hunt-product-select">
              <SelectValue placeholder="Select product…" />
            </SelectTrigger>
            <SelectContent className="bg-navy2 border-cyan/25 text-pale">
              {PRODUCTS.map((p) => (
                <SelectItem key={p} value={p} className="text-xs font-mono focus:bg-teal/30 focus:text-paper">{p}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      );
    if (mIdx === 1 && sIdx === 1)
      return (
        <div data-testid="hunt-m2-step2">
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={reagents} margin={{ left: -14, right: 8, top: 4 }}>
              {GRID}
              <XAxis dataKey="lot" {...axisProps} interval={0} angle={-45} textAnchor="end" height={60} />
              <YAxis domain={[7, 11]} {...axisProps} />
              <Tooltip {...tooltipStyle} formatter={(v) => [`${v} min`, "Mean migration time"]} />
              <Bar dataKey="mean" name="Mean migration time" cursor="pointer" fill="#20B8CD" onClick={(d) => (d && d.lot === "RGT-107" ? correct() : wrong())} />
            </BarChart>
          </ResponsiveContainer>
          <p className="font-mono text-[10px] text-steel mt-1">Mean migration time by reagent lot (RapidSDS-X). Click the shifted lot.</p>
        </div>
      );
    if (mIdx === 1 && sIdx === 2)
      return (
        <div className="panel p-5 bg-navy" data-testid="hunt-m2-step3">
          <p className="font-mono text-xs text-cyan">RGT-107 vs fleet (RapidSDS-X, synthetic spec 7.2–9.2 min)</p>
          <div className="grid grid-cols-3 gap-3 mt-3 font-mono text-center">
            <div><p className="text-[10px] text-steel">RGT-107 mean</p><p className="text-xl text-warn">{rgt107.mean}</p></div>
            <div><p className="text-[10px] text-steel">Fleet mean</p><p className="text-xl text-cyan">{fleetMean}</p></div>
            <div><p className="text-[10px] text-steel">RGT-107 Cpk</p><p className="text-xl text-warn">{rgt107.cpk}</p></div>
          </div>
          <p className="text-sm text-pale/85 mt-3 leading-relaxed">
            A tight distribution centred far above the fleet — a systematic shift, not scatter. Against the synthetic demonstration window its Cpk is
            negative while other lots sit comfortably inside.
          </p>
          <button onClick={correct} data-testid="hunt-m2-confirm" className="mt-4 bg-cyan text-navy font-sans font-semibold text-sm px-5 py-2.5 rounded-sm hover:bg-pale transition-colors">
            Confirm the shift
          </button>
        </div>
      );
    if (mIdx === 1 && sIdx === 3)
      return (
        <div className="panel p-5 bg-navy" data-testid="hunt-m2-step4">
          <p className="font-mono text-xs text-cyan">Cross-check before concluding</p>
          <p className="text-sm text-pale/85 mt-2 leading-relaxed">
            Instrument spreads for RapidSDS-X overlap heavily (see the CI case study box plots), and the shift tracks the reagent lot across months — so
            neither one instrument nor one isolated event explains it. The evidence supports a lot-linked process/specification alignment question.
          </p>
          <button onClick={correct} data-testid="hunt-m2-complete" className="mt-4 bg-cyan text-navy font-sans font-semibold text-sm px-5 py-2.5 rounded-sm hover:bg-pale transition-colors">
            Cross-check complete — finish
          </button>
        </div>
      );
    // Mission 3 — CMP-209 / NovaFlow-FX microbubble artifacts
    if (mIdx === 2 && sIdx === 0)
      return (
        <div data-testid="hunt-m3-step1">
          <p className="text-xs text-steel mb-3">Set the product filter exactly as you would in the Analytics Lab.</p>
          <Select value={product} onValueChange={(v) => { setProduct(v); if (v === "NovaFlow-FX") correct(); else wrong(); }}>
            <SelectTrigger className="bg-navy border-teal/40 text-pale text-xs h-9 w-64" data-testid="hunt-product-select-m3">
              <SelectValue placeholder="Select product…" />
            </SelectTrigger>
            <SelectContent className="bg-navy2 border-cyan/25 text-pale">
              {PRODUCTS.map((p) => (
                <SelectItem key={p} value={p} className="text-xs font-mono focus:bg-teal/30 focus:text-paper">{p}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      );
    if (mIdx === 2 && sIdx === 1)
      return (
        <div data-testid="hunt-m3-step2">
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={cmpLots} margin={{ left: -14, right: 8, top: 4 }}>
              {GRID}
              <XAxis dataKey="lot" {...axisProps} interval={0} angle={-45} textAnchor="end" height={60} />
              <YAxis {...axisProps} />
              <Tooltip {...tooltipStyle} formatter={(v, name, item) => [`${v}% (${item.payload.artifacts} of ${item.payload.runs} runs)`, "Artifact rate"]} />
              <Bar dataKey="rate" name="Artifact rate %" cursor="pointer" fill="#20B8CD" onClick={(d) => (d && d.lot === "CMP-209" ? correct() : wrong())} />
            </BarChart>
          </ResponsiveContainer>
          <p className="font-mono text-[10px] text-steel mt-1">Microbubble-artifact rate by component lot (NovaFlow-FX). Click the lot that stands out.</p>
        </div>
      );
    if (mIdx === 2 && sIdx === 2)
      return (
        <div className="panel p-5 bg-navy" data-testid="hunt-m3-step3">
          <p className="font-mono text-xs text-cyan">CMP-209 concentration check</p>
          <p className="text-sm text-pale/85 mt-2 leading-relaxed">
            CMP-209 carries {((cmp209.artifacts / totalArtifacts) * 100).toFixed(0)}% of all NovaFlow-FX microbubble-artifact failures ({cmp209.artifacts}{" "}
            of {totalArtifacts}) at a {cmp209.rate}% rate — every other lot sits near zero. The signal is concentrated, not evenly distributed.
          </p>
          <button onClick={correct} data-testid="hunt-m3-confirm" className="mt-4 bg-cyan text-navy font-sans font-semibold text-sm px-5 py-2.5 rounded-sm hover:bg-pale transition-colors">
            Confirm the lot effect
          </button>
        </div>
      );
    if (mIdx === 2 && sIdx === 3)
      return (
        <div data-testid="hunt-m3-step4">
          <ResponsiveContainer width="100%" height={200}>
            <BarChart data={cmp209ByInst} margin={{ left: -18, right: 8, top: 4 }}>
              {GRID}
              <XAxis dataKey="instrument" {...axisProps} />
              <YAxis {...axisProps} />
              <Tooltip {...tooltipStyle} formatter={(v) => [v, "CMP-209 artifact failures"]} />
              <Bar dataKey="count" name="Artifacts" fill="#137C8B" />
            </BarChart>
          </ResponsiveContainer>
          <p className="text-xs text-steel mt-2 leading-relaxed">
            CMP-209 artifacts spread across instruments and months — no single instrument or short time window explains the pattern. The lot association
            survives the challenge, but remains an RCA lead, not proven causation.
          </p>
          <button onClick={correct} data-testid="hunt-m3-challenge" className="mt-4 bg-cyan text-navy font-sans font-semibold text-sm px-5 py-2.5 rounded-sm hover:bg-pale transition-colors">
            Competing explanations challenged
          </button>
        </div>
      );
    return (
      <div className="panel p-5 bg-navy" data-testid="hunt-m3-step5">
        <p className="font-mono text-xs text-cyan">Initial vs retest behaviour</p>
        <p className="text-sm text-pale/85 mt-2 leading-relaxed">
          Microbubble-artifact retests recover about {mbRecovery ? mbRecovery.recovery.toFixed(0) : "—"}% of the time ({mbRecovery?.retests} retests in the
          synthetic data) — partial recovery consistent with an intermittent, state-dependent fluid-path phenomenon rather than a permanently failed unit.
        </p>
        <button onClick={correct} data-testid="hunt-m3-complete" className="mt-4 bg-cyan text-navy font-sans font-semibold text-sm px-5 py-2.5 rounded-sm hover:bg-pale transition-colors">
          Evidence converges — complete mission
        </button>
      </div>
    );
  };

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 py-14" data-testid="pattern-hunt">
      <div className="no-print">
        <SectionHeader overline="Analytics Lab — Guided Challenge" title={hunt.title} description={hunt.objective} />
        <div className="mt-4 max-w-xl">
          <Disclaimer>
            Runs on the same deterministic seeded dataset as the Analytics Lab — nothing is randomized per refresh. Association is evidence for
            investigation, not proof of causality.
          </Disclaimer>
        </div>
      </div>

      {phase === "intro" && (
        <div className="panel p-8 mt-8 text-center no-print" data-testid="hunt-intro">
          <Target className="w-8 h-8 text-cyan mx-auto" aria-hidden="true" />
          <p className="text-pale/85 text-base mt-4 max-w-xl mx-auto leading-relaxed">
            Three signals are deliberately embedded in this synthetic dataset. Your job: find them the way an investigator would — start broad, stratify,
            challenge competing explanations, and verify. No answers are revealed until you attempt the analysis.
          </p>
          <div className="flex flex-wrap justify-center gap-2 mt-5">
            {MISSIONS.map((m, i) => (
              <span key={m.signalId} className="tech-tag" data-testid={`hunt-intro-chip-${i}`}>
                Mission {i + 1} — {["Instrument signal", "Reagent lot signal", "Component lot signal"][i]} · {MISSION_META[i].difficulty} · {MISSION_META[i].time}
              </span>
            ))}
          </div>
          <button
            data-testid="hunt-start-button"
            onClick={() => setPhase("play")}
            className="mt-6 bg-cyan text-navy font-sans font-semibold text-sm px-6 py-3 rounded-sm hover:bg-pale transition-colors"
          >
            {hunt.entryCta}
          </button>
        </div>
      )}

      {phase === "play" && (
        <div className="mt-8 no-print" data-testid="hunt-play">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div className="flex flex-wrap gap-2">
              {MISSIONS.map((m, i) => (
                <button
                  key={m.signalId}
                  data-testid={`hunt-mission-chip-${i}`}
                  onClick={() => { setMIdx(i); setSIdx(0); setNudge(""); setShowHint(false); setProduct(""); }}
                  className={`font-mono text-[10px] px-2.5 py-1 rounded-sm border transition-colors ${
                    doneM[i] ? "border-success/50 text-success" : i === mIdx ? "border-cyan text-cyan" : "border-teal/30 text-steel hover:text-pale"
                  }`}
                >
                  {doneM[i] ? "✓ " : ""}Mission {i + 1}
                  <span className="block text-[8px] font-normal opacity-80 mt-0.5">
                    {MISSION_META[i].difficulty} • {MISSION_META[i].time}
                  </span>
                </button>
              ))}
            </div>
            <div className="flex items-center gap-2">
              <button data-testid="hunt-copy-link" onClick={() => copyToClipboard(window.location.href, "Pattern Hunt link")} className="flex items-center gap-1.5 font-mono text-[11px] text-pale/70 hover:text-cyan transition-colors">
                <Link2 className="w-3.5 h-3.5" aria-hidden="true" /> Share
              </button>
              <button data-testid="hunt-reset" onClick={reset} className="flex items-center gap-1.5 font-mono text-[11px] text-pale/70 hover:text-cyan transition-colors">
                <RotateCcw className="w-3.5 h-3.5" aria-hidden="true" /> Reset
              </button>
            </div>
          </div>
          <div className="h-1 bg-navy2 rounded-full overflow-hidden mt-3" aria-hidden="true">
            <div className="h-full bg-cyan transition-all duration-500" style={{ width: `${(stepsDone / totalSteps) * 100}%` }} />
          </div>

          <div className="panel p-6 mt-5">
            <p className="overline-label">
              Mission {mIdx + 1} — Step {step.step} of {mission.path.length}
            </p>
            <p className="font-sans text-lg text-paper mt-2" data-testid="hunt-step-prompt">{step.prompt}</p>
            <div className="mt-5">{stepBody()}</div>
            {nudge && <p className="text-warn text-xs font-body mt-4" data-testid="hunt-nudge">{nudge}</p>}
            <div className="flex items-center gap-4 mt-5 pt-4 border-t border-cyan/10">
              <button
                data-testid="hunt-hint-button"
                onClick={() => setShowHint((h) => !h)}
                className="flex items-center gap-1.5 font-mono text-xs text-cyan hover:text-paper transition-colors"
              >
                <Lightbulb className="w-3.5 h-3.5" aria-hidden="true" /> {showHint ? "Hide hint" : "Hint"}
              </button>
              <button data-testid="hunt-show-answer" onClick={showAnswer} className="flex items-center gap-1.5 font-mono text-xs text-steel hover:text-warn transition-colors">
                <Eye className="w-3.5 h-3.5" aria-hidden="true" /> Show answer
              </button>
            </div>
            {showHint && (
              <p className="text-xs text-cyan/90 font-body mt-3" data-testid="hunt-hint-text">Hint: {step.action}</p>
            )}
            {revealed[mIdx] && (
              <p className="text-xs text-warn font-body mt-3" data-testid="hunt-revealed">{mission.reveal}</p>
            )}
          </div>
        </div>
      )}

      {phase === "done" && (
        <>
          <div className="panel p-8 mt-8 border-l-2 border-l-success no-print" data-testid="hunt-success">
          <div className="flex items-center gap-2">
            <CheckCircle2 className="w-6 h-6 text-success" aria-hidden="true" />
            <h3 className="font-sans tracking-tight text-2xl text-paper">All three signals found</h3>
          </div>
          <p className="text-pale/85 text-sm mt-4 leading-relaxed max-w-2xl">{hunt.successMessage}</p>
          <div className="grid md:grid-cols-3 gap-4 mt-6">
            {MISSIONS.map((m, i) => (
              <div key={m.signalId} className="panel p-4 bg-navy" data-testid={`hunt-lesson-${i}`}>
                <p className="font-mono text-xs text-cyan">{m.secretTarget} — {m.failureMode || `${m.product} / ${m.metric}`}</p>
                <p className="text-xs text-pale/80 mt-2 leading-relaxed">{m.reveal}</p>
                <p className="text-xs text-steel mt-2 leading-relaxed italic">{m.lesson}</p>
              </div>
            ))}
          </div>
          <div className="mt-5 max-w-2xl">
            <Disclaimer tone="warn">{hunt.scientificGuardrail}</Disclaimer>
          </div>
          <div className="flex flex-wrap gap-3 mt-6">
            <a href="/#analytics-lab" data-testid="hunt-open-lab" className="bg-cyan text-navy font-sans font-semibold text-sm px-5 py-2.5 rounded-sm hover:bg-pale transition-colors">
              Open the full Analytics Lab
            </a>
            <button onClick={() => copyToClipboard(window.location.href, "Pattern Hunt link")} data-testid="hunt-copy-done" className="border border-cyan/50 text-cyan font-sans text-sm px-5 py-2.5 rounded-sm hover:bg-cyan/10 transition-colors">
              Copy share link
            </button>
            <button onClick={reset} data-testid="hunt-reset-done" className="border border-teal/40 text-pale/80 font-sans text-sm px-5 py-2.5 rounded-sm hover:border-cyan/60 hover:text-paper transition-colors">
              Reset challenge
            </button>
          </div>
          </div>

          <div className="print-page panel p-8 mt-6 border-2 border-cyan/40" data-testid="hunt-certificate">
            <p className="overline-label print-accent">Poojan Patel</p>
            <h3 className="font-sans tracking-tight text-2xl text-paper mt-2">Pattern Hunt — Completed</h3>
            <p className="text-xs text-steel mt-1">
              Guided signal-discovery challenge on a deterministic synthetic manufacturing dataset
            </p>
            <div className="mt-5 space-y-2.5">
              {MISSIONS.map((m, i) => (
                <div key={m.signalId} className="flex items-start gap-2 text-xs text-pale/85" data-testid={`certificate-mission-${i}`}>
                  <CheckCircle2 className="w-3.5 h-3.5 text-success mt-0.5 shrink-0 print-accent" aria-hidden="true" />
                  <span>
                    <span className="font-mono text-cyan print-accent">{m.secretTarget}</span> — {m.failureMode || `${m.product} / ${m.metric}`}{" "}
                    <span className="text-steel">
                      ({MISSION_META[i].difficulty} • {MISSION_META[i].time})
                    </span>
                  </span>
                </div>
              ))}
            </div>
            <p className="font-mono text-[10px] text-steel mt-6 leading-relaxed">
              Completed {new Date().toLocaleDateString("en-CA")} • Association is evidence for investigation, not proof of causality •
              PoojanQMSAutomation.com
            </p>
            <div className="flex flex-wrap gap-3 mt-5 no-print">
              <button
                data-testid="certificate-print-button"
                onClick={() => window.print()}
                className="bg-cyan text-navy font-sans font-semibold text-sm px-5 py-2.5 rounded-sm hover:bg-pale transition-colors"
              >
                Save Certificate as PDF
              </button>
              <button
                data-testid="certificate-copy-link"
                onClick={() => copyToClipboard(window.location.href, "Completion link")}
                className="border border-cyan/50 text-cyan font-sans text-sm px-5 py-2.5 rounded-sm hover:bg-cyan/10 transition-colors"
              >
                Copy completion link
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
